import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { boFetch } from "@/lib/backoffice.server";

export type PublicArticle = {
  id: string;
  slug: string;
  category: string;
  title: string;
  excerpt: string;
  content: string[];
  author: string;
  cover_url: string | null;
  reading_time: string;
  published_at: string | null;
};

export type PublicProduct = {
  id: string;
  slug: string;
  name: string;
  description: string;
  long_description: string;
  price_cents: number;
  currency: string;
  sort_order: number;
  form_key?: string | null;
  show_in_catalog?: boolean | null;
  active?: boolean | null;
};

export type PublicSettings = {
  consultation_payment_enabled: boolean;
  consultation_price_cents: number;
};

export const listArticlesPublic = createServerFn({ method: "POST" })
  .inputValidator((input) =>
    z.object({ locale: z.enum(["es", "pt", "en"]).optional() }).parse(input ?? {}),
  )
  .handler(async ({ data }) => {
    const res = await boFetch<{ articles: PublicArticle[]; locale?: string }>(
      "/api/public/v1/articles",
      { query: { locale: data.locale } },
    );
    return { articles: res.articles ?? [], locale: res.locale ?? data.locale ?? null };
  });

export const getArticlePublic = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(200) }).parse(input))
  .handler(async ({ data }) => {
    try {
      const res = await boFetch<{ article: PublicArticle }>(
        `/api/public/v1/articles/${encodeURIComponent(data.slug)}`,
      );
      return { article: res.article ?? null };
    } catch (e) {
      if ((e as { status?: number }).status === 404) return { article: null };
      throw e;
    }
  });

export const listProductsPublic = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const res = await boFetch<{ products: PublicProduct[] }>("/api/public/v1/products", {
      cache: { freshSec: 60, staleSec: 7 * 24 * 60 * 60 },
    });
    return { products: res.products ?? [] };
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status !== 404) console.error("listProductsPublic failed", e);
    return { products: [] as PublicProduct[] };
  }
});

// Returns all products including those hidden from the public catalog
// (show_in_catalog = false). Useful for selecting a product by form_key
// where the catalog visibility is irrelevant (e.g. the /contact route
// decides which form to render based on the product's `active` flag).
export const listAllProductsPublic = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const res = await boFetch<{ products: PublicProduct[] }>("/api/public/v1/products", {
      cache: { freshSec: 60, staleSec: 7 * 24 * 60 * 60 },
      query: { include_hidden: "true" },
    });
    return { products: res.products ?? [] };
  } catch (e) {
    const status = (e as { status?: number }).status;
    if (status !== 404) console.error("listAllProductsPublic failed", e);
    return { products: [] as PublicProduct[] };
  }
});

export const getProductPublic = createServerFn({ method: "POST" })
  .inputValidator((input) => z.object({ slug: z.string().min(1).max(200) }).parse(input))
  .handler(async ({ data }) => {
    try {
      const res = await boFetch<{ product: PublicProduct }>(
        `/api/public/v1/products/${encodeURIComponent(data.slug)}`,
      );
      return { product: res.product ?? null };
    } catch (e) {
      const status = (e as { status?: number }).status;
      if (status !== 404) console.error("getProductPublic failed", e);
      return { product: null };
    }
  });

export const getPublicSettings = createServerFn({ method: "GET" }).handler(async () => {
  const res = await boFetch<{ settings: Record<string, unknown> }>("/api/public/v1/site-settings");
  const s = res.settings ?? {};
  return {
    settings: {
      consultation_payment_enabled: Boolean(s.consultation_payment_enabled ?? true),
      consultation_price_cents: Number(s.consultation_price_cents ?? 9900),
    } as PublicSettings,
  };
});

// ---------------------------------------------------------------------------
// CMS bundles: text content (i18n) + media assets, both managed in backoffice.
// Returns empty bundles gracefully if the endpoints aren't deployed yet, so
// the site keeps rendering with its bundled fallbacks.
// ---------------------------------------------------------------------------

export type Locale = "es" | "pt" | "en";

export type ContentBundle = {
  es: Record<string, string>;
  pt: Record<string, string>;
  en: Record<string, string>;
};

export type MediaAsset = {
  url: string;
  kind: "image" | "video";
  alt: { es: string; pt: string; en: string };
};

export type MediaBundle = Record<string, MediaAsset>;

const EMPTY_CONTENT: ContentBundle = { es: {}, pt: {}, en: {} };

export const getContentBundle = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const res = await boFetch<{ blocks: ContentBundle }>("/api/public/v1/content");
    return {
      blocks: {
        es: res.blocks?.es ?? {},
        pt: res.blocks?.pt ?? {},
        en: res.blocks?.en ?? {},
      } satisfies ContentBundle,
    };
  } catch (e) {
    if ((e as { status?: number }).status !== 404) {
      console.error("getContentBundle failed", e);
    }
    return { blocks: EMPTY_CONTENT };
  }
});

export const getMediaBundle = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const res = await boFetch<{ assets: MediaBundle }>("/api/public/v1/media");
    return { assets: res.assets ?? {} };
  } catch (e) {
    if ((e as { status?: number }).status !== 404) {
      console.error("getMediaBundle failed", e);
    }
    return { assets: {} as MediaBundle };
  }
});

export const getLocales = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const res = await boFetch<{ locales: Locale[] }>("/api/public/v1/locales");
    const valid: Locale[] = ["es", "pt", "en"];
    const filtered = (res.locales ?? []).filter((l): l is Locale => valid.includes(l as Locale));
    return { locales: filtered.length ? filtered : valid };
  } catch (e) {
    if ((e as { status?: number }).status !== 404) {
      console.error("getLocales failed", e);
    }
    return { locales: ["es", "pt", "en"] as Locale[] };
  }
});

export type ThemeTokens = Record<string, string>;
export type ThemeMeta = Record<string, { category?: string }>;

export const getThemeTokens = createServerFn({ method: "GET" }).handler(async () => {
  try {
    const res = await boFetch<{ tokens: ThemeTokens; meta?: ThemeMeta }>(
      "/api/public/v1/theme",
    );
    return { tokens: res.tokens ?? {}, meta: res.meta ?? {} };
  } catch (e) {
    if ((e as { status?: number }).status !== 404) {
      console.error("getThemeTokens failed", e);
    }
    return { tokens: {} as ThemeTokens, meta: {} as ThemeMeta };
  }
});

// ---------------------------------------------------------------------------
// Favicon bundle managed in the backoffice. `files` maps each icon filename
// to its real storage URL (proxied by our root-level routes, never exposed in
// the HTML). `links`/`manifestIcons` carry RELATIVE hrefs served from our own
// domain; `absoluteHref`/`absoluteSrc` are fallbacks we intentionally ignore.
// Returns an empty bundle when no favicon has been published yet so the site
// keeps using its bundled fallback icons.
// ---------------------------------------------------------------------------

export type FaviconLink = {
  rel: string;
  href: string;
  absoluteHref?: string;
  sizes?: string;
  type?: string;
};

export type ManifestIcon = {
  src: string;
  absoluteSrc?: string;
  sizes: string;
  type?: string;
};

export type FaviconBundle = {
  files: Record<string, string>;
  links: FaviconLink[];
  manifestIcons: ManifestIcon[];
};

const EMPTY_FAVICON: FaviconBundle = { files: {}, links: [], manifestIcons: [] };

export const getFavicon = createServerFn({ method: "GET" }).handler(async () => {
  try {
    // ~5 min server-side cache so icon proxy routes don't hit the API on
    // every single icon request.
    const res = await boFetch<FaviconBundle>("/api/public/v1/favicon", {
      cache: { freshSec: 300, staleSec: 7 * 24 * 60 * 60 },
    });
    return {
      files: res.files && typeof res.files === "object" ? res.files : {},
      links: Array.isArray(res.links) ? res.links : [],
      manifestIcons: Array.isArray(res.manifestIcons) ? res.manifestIcons : [],
    } satisfies FaviconBundle;
  } catch (e) {
    if ((e as { status?: number }).status !== 404) {
      console.error("getFavicon failed", e);
    }
    return EMPTY_FAVICON;
  }
});
